import { useApolloClient } from "@apollo/client"
import {
  CoverPageOfferFragment,
  HomeRegistrationsDocument,
  RegistrationAccessesDocument,
  RegistrationRoleEnum,
  useCreateCurriculumLicenseOrderMutation,
  useResourceLicenseQuery
} from "@pathwright/graphql-client/src/generated"
import { slugifyItem } from "@pathwright/utils/src/text/slug"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Link from "@pathwright/ux/src/components/link/Link"
import { useCallback, useEffect, useMemo } from "react"
import { usePollForOrderFulfillment } from "../order/useOrderPollingQuery"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getGroupUrl } from "../utils/urls"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageTranslate } from "./utils"

/**
 * In-place Path licensing for a Cover Page Path Offer (connected licensee
 * space). On the licensor school, callers should open signup instead.
 * Safe to call for non-Path Offers — create is a no-op when unsupported.
 */
export function useCreatePathFromOffer(offer: CoverPageOfferFragment) {
  const client = useApolloClient()
  const { t } = useCoverPageTranslate()
  const { school } = usePathwrightContext()
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const canPlaceOrder =
    !!offer.resource_license_offering_id && school.id !== offer.school_id
  const signupHref = offer.resource_license_offering_id
    ? `/sign-up/?license=${offer.resource_license_offering_id}`
    : "/sign-up/"

  const newPathName = paintResource?.name || ""
  const canCreate = !!offer.resource_license_offering_id

  // 1. Create the order to license the new path.
  const [orderMutation, orderMutationState] =
    useCreateCurriculumLicenseOrderMutation({
      variables: {
        name: newPathName,
        license_offering_id: offer.resource_license_offering_id!,
        licensor_school_id: offer.school_id,
        licensee_school_id: school.id,
        role: RegistrationRoleEnum.Teacher
      }
    })

  const orderId =
    orderMutationState.data?.createCurriculumLicenseOrder?.id ?? null

  // 2. Poll for the order to be fulfilled.
  const [orderFulfilled, orderQuery] = usePollForOrderFulfillment(orderId, {
    pollInterval: 1000
  })
  const order = orderQuery.data?.order

  useEffect(() => {
    if (orderFulfilled) {
      client.refetchQueries({
        include: [HomeRegistrationsDocument, RegistrationAccessesDocument]
      })
    }
  }, [client, orderFulfilled])

  // 3. Query the new resource and cohort that were created by the order.
  const resourceQuery = useResourceLicenseQuery({
    variables: {
      id: order?.fulfilled_object_id!
    },
    skip: !order || !order.fulfilled_object_id
  })
  const newCohort = resourceQuery.data?.resource_license?.group
  const newResource = newCohort?.resource

  const loading = orderMutationState.loading || (!!orderId && !newResource)

  const newResourceLink = useMemo(() => {
    if (newCohort && newResource) {
      return getGroupUrl(slugifyItem(newResource), newCohort.id)
    }
    return undefined
  }, [newCohort, newResource])

  useEffect(() => {
    if (newResource && newResourceLink) {
      // 4. Show a success toast with a link to the new path.
      toaster.create({
        title: t("path_offer.added_to_learning_space", {
          defaultValue: "{{name}} added to your Learning Space",
          name: newResource.name
        }),
        description: (
          <DarkMode>
            <Link to={newResourceLink}>
              {t("toolbar.view_path", { defaultValue: "View Path" })}
            </Link>
          </DarkMode>
        ),
        type: "success",
        duration: 6000,
        closable: true
      })
    }
  }, [newResource, newResourceLink, t])

  const create = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (!canCreate) return
      if (!canPlaceOrder) {
        window.open(signupHref, "_blank", "noopener,noreferrer")
        return
      }
      if (!newResourceLink) {
        void orderMutation()
      }
    },
    [canCreate, canPlaceOrder, newResourceLink, orderMutation, signupHref]
  )

  return {
    canPlaceOrder,
    signupHref,
    create,
    loading,
    loadingText: t("path_offer.copying_path", {
      defaultValue: "Copying Path..."
    }),
    newResourceLink,
    viewPathLabel: t("toolbar.view_path", { defaultValue: "View Path" })
  }
}
